import React, { useState } from 'react'
import { EnvelopeIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router'
import NotificationIcon from '../NotificationIcon'
import RightSlideOver from '../RightSlideOver'

// The notification bell + its right-side drawer, shared by carbon and ppa. The
// shell (bell, unread dot, "no notifications" empty state, per-row link with the
// notification icon, and the "View all" footer) is identical across apps; only the
// per-domain summary line differs, so it is supplied by the caller via `renderLine`.
// The subscription/query wiring stays in each app's thin wrapper (it depends on
// app-local services and query keys).
export interface NotificationBellProps<T extends { id: string }> {
  notifications: T[] | undefined
  // Renders the domain-specific summary line for one notification (carbon: quantity
  // / price / vintage; ppa: capacity / price / start). Wrapped by the shared link.
  renderLine: (notification: T) => React.ReactNode
}

function NotificationBell<T extends { id: string }> ({ notifications, renderLine }: NotificationBellProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const hasNotifications = Boolean(notifications?.length)

  const content = (
    <div className="divide-y divide-gray-200">
      {!hasNotifications && (
        <div className="py-4 text-sm text-gray-500">There are no notifications at this time</div>
      )}

      {notifications?.map((notification) => (
        <Link
          key={notification.id}
          to={`/app/notifications/${notification.id}`}
          onClick={() => setIsOpen(false)}
          className="-mx-4 sm:-mx-6 block px-4 sm:px-6 py-4 text-sm text-gray-700 hover:bg-gray-50"
        >
          <div className="flex items-center">
            <div className="shrink-0">
              <NotificationIcon notification={{ status: 'info' }} />
            </div>
            <div className="ml-3 font-medium">{renderLine(notification)}</div>
          </div>
        </Link>
      ))}

      {hasNotifications && (
        <Link
          to="/app/notifications"
          onClick={() => setIsOpen(false)}
          className="-mx-4 sm:-mx-6 block px-4 sm:px-6 py-4 text-sm font-medium text-perfect-shine hover:bg-gray-50"
        >
          View all notifications
        </Link>
      )}
    </div>
  )

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="bg-white p-1 text-gray-400 hover:text-bonfire-dark-light"
      >
        <span className="sr-only">View notifications</span>
        <EnvelopeIcon className="h-8 w-8" aria-hidden="true" />
      </button>
      {hasNotifications && (
        <span className="absolute flex h-2 w-2 pointer-events-none" style={{ top: '5px', right: '11px' }}>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-cerise-red"></span>
        </span>
      )}

      <RightSlideOver
        title="Notifications"
        content={content}
        isOpen={isOpen}
        setOpen={setIsOpen}
        size="md"
      />
    </div>
  )
}

export default NotificationBell
